import HeaderSection from "../Header/HeaderSection.vue";
import BolideAssistant from "@/components/BolideAssistant/BolideAssistant.vue";
import PortfolioSidebar from "@/components/PortfolioSidebar/PortfolioSidebar.vue";
import PropertySidebar from "@/components/PropertySidebar/PropertySidebar.vue";
import MiningSidebar from "@/components/MiningSidebar/MiningSidebar.vue";
import sidebar_page_context from "@/components/sidebar_page_context";
import { get_user_role_name } from "@/utils/auth_user";
import { get_user_profile, fetch_and_store_user_profile, get_token } from '@/router';

export default {
    name: "ProfileSection",

    mixins: [sidebar_page_context],

    components: {
        HeaderSection,
        BolideAssistant,
        PortfolioSidebar,
        PropertySidebar,
        MiningSidebar
    },

    data() {
        return ({
            showAssistant: false,
            is_mobile: window.innerWidth < 768,
            mobile_sidebar_open: false,
            sidebar_collapsed: true,
            breadcrumb: [
                { id: "home", title: "Dashboard" },
                { id: "profile", title: "Profile" },
            ],
            userProfile: null,
            loading: false,
            error: null,
            show_error_snackbar: false,
            snackbar_timeout_id: null,
            rating: 4.8,
            reviewCount: 0,
        });
    },

    computed: {
        // All computed properties remain the same, but now use the globally stored profile
        avatar_initials() {
            if (!this.userProfile) return 'UR';
            const first = this.userProfile.first_name?.charAt(0) || '';
            const last = this.userProfile.last_name?.charAt(0) || '';
            return (first + last).toUpperCase() || 'UR';
        },

        fullName() {
            if (!this.userProfile) return 'Loading...';
            const first = this.userProfile.first_name || '';
            const last = this.userProfile.last_name || '';
            return `${first} ${last}`.trim() || 'User';
        },
        
        username() {
            return this.userProfile?.username ? `@${this.userProfile.username}` : '@user';
        },

        joinYear() {
            if (this.userProfile?.date_joined) {
                return new Date(this.userProfile.date_joined).getFullYear();
            }
            return new Date().getFullYear();
        },

        userRole() {
            return get_user_role_name(this.userProfile) || 'User';
        },

        userEmail() {
            return this.userProfile?.email || 'user@example.org';
        },

        userPhone() {
            return this.userProfile?.phone_number || 'Not provided';
        },

        profileProgress() {
            if (!this.userProfile) return 0;
            
            let completed = 0;
            const totalFields = 4;
            
            // Email is always considered verified/completed
            completed++; 
            if (this.userProfile.phone_number) completed++;
            if (this.userProfile.first_name) completed++;
            if (this.userProfile.last_name) completed++;
            
            return Math.round((completed / totalFields) * 100);
        },

        remainingItems() {
            if (!this.userProfile) return 4;
            
            let incomplete = 0;
            if (!this.userProfile.phone_number) incomplete++;
            if (!this.userProfile.first_name) incomplete++;
            if (!this.userProfile.last_name) incomplete++;
            
            // Email is handled as always verified per requirements
            return incomplete;
        },

        isEmailVerified() {
            // Requirement: Always return true
            return true;
        },

        hasPhoneNumber() {
            return !!this.userProfile?.phone_number;
        }
    },

    mounted() {
        window.addEventListener("resize", this.handle_resize);
        this.loadUserProfile();
    },

    beforeUnmount() {
        window.removeEventListener("resize", this.handle_resize);
        this.clear_snackbar_timer();
    },

    watch: {
        error(message) {
            if (message) {
                this.show_error_snackbar = true;
                this.start_snackbar_timer();
            }
        }
    },

    methods: {
        handle_resize() {
            this.is_mobile = window.innerWidth < 768;
            if (!this.is_mobile) this.mobile_sidebar_open = false;
        },

        toggle_mobile_sidebar() {
            this.mobile_sidebar_open = !this.mobile_sidebar_open;
        },

        clear_snackbar_timer() {
            if (this.snackbar_timeout_id) {
                clearTimeout(this.snackbar_timeout_id);
                this.snackbar_timeout_id = null;
            }
        },

        start_snackbar_timer() {
            this.clear_snackbar_timer();
            this.snackbar_timeout_id = setTimeout(() => {
                this.show_error_snackbar = false;
                this.snackbar_timeout_id = null;
            }, 5000);
        },

        clear_error() {
            this.clear_snackbar_timer();
            this.show_error_snackbar = false;
        },

        handleBreadcrumbClick(id) {
            if (id === "home") {
                this.$router.push("/home");
            }
        },

        async loadUserProfile() {
            this.loading = true;
            this.error = null;

            try {
                // First try to get profile from sessionStorage
                let profile = get_user_profile();
                
                // If not found, fetch from API
                if (!profile) {
                    profile = await fetch_and_store_user_profile();
                }
                
                if (profile) {
                    // Fetch phone_number from UserProfile API
                    const update_profile_url = process.env.VUE_APP_UPDATE_USER_URL;
                    const raw_user_id = profile.id || profile.user_id;
                    const user_id = Number.parseInt(raw_user_id, 10);
                    const auth_token = get_token();
                    
                    if (Number.isInteger(user_id) && auth_token && update_profile_url) {
                        try {
                            const base_url = update_profile_url.endsWith("/")
                                ? update_profile_url
                                : `${update_profile_url}/`;
                            const profile_url = `${base_url}${user_id}/`;
                            
                            const response = await fetch(profile_url, {
                                method: "GET",
                                headers: {
                                    'Authorization': `Token ${auth_token}`,
                                    'Content-Type': 'application/json',
                                }
                            });
                            
                            if (response.status === 200) {
                                const profile_data = await response.json();
                                // Merge phone_number from UserProfile into profile
                                profile.phone_number = profile_data.phone_number;
                            }
                        } catch (error) {
                            console.log("Failed to fetch phone number: " + error);
                        }
                    }
                    
                    this.userProfile = profile;
                } else {
                    throw new Error('Failed to load user profile');
                }
            } catch (error) {
                this.error = error.message || 'Failed to load profile data. Please try again.';
                console.error('Profile loading error:', error);
            } finally {
                this.loading = false;
            }
        },

        // Optional: Method to refresh profile data
        async refreshProfile() {
            const freshProfile = await fetch_and_store_user_profile();
            if (freshProfile) {
                // Fetch phone_number from UserProfile API
                const update_profile_url = process.env.VUE_APP_UPDATE_USER_URL;
                const raw_user_id = freshProfile.id || freshProfile.user_id;
                const user_id = Number.parseInt(raw_user_id, 10);
                const auth_token = get_token();
                
                if (Number.isInteger(user_id) && auth_token && update_profile_url) {
                    try {
                        const base_url = update_profile_url.endsWith("/")
                            ? update_profile_url
                            : `${update_profile_url}/`;
                        const profile_url = `${base_url}${user_id}/`;
                        
                        const response = await fetch(profile_url, {
                            method: "GET",
                            headers: {
                                'Authorization': `Token ${auth_token}`,
                                'Content-Type': 'application/json',
                            }
                        });
                        
                        if (response.status === 200) {
                            const profile_data = await response.json();
                            // Merge phone_number from UserProfile into profile
                            freshProfile.phone_number = profile_data.phone_number;
                        }
                    } catch (error) {
                        console.log("Failed to fetch phone number: " + error);
                    }
                }
                
                this.userProfile = freshProfile;
            }
        }
    }
};
